import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    
    // Handle click outside to close dropdown
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutsideMobile = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && isOpen) {
        setIsOpen(false);
        setMobileDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideMobile);
    return () => document.removeEventListener('mousedown', handleClickOutsideMobile);
  }, [isOpen]);

  const contactInfo = {
    phone: "+91 7080100324",
    email: "bnsmahilamahavidyalaya@gmail.com",
    collegeCode: "1120"
  };

  const navItems = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about",
      subItems: [
        { name: "Introduction", href: "/introduction" },
        { name: "Mission & Vision", href: "/mision-vision" },
        { name: "Principal's Message", href: "/principal" },
        { name: "Fee Refund Policy", href: "/free-refund" }
      ] 
    },
    { name: "Admission", href: "/admission",
      subItems: [
        { name: "Course Detail", href: "/course-details" },
        { name: "Director's Message", href: "/director" },
        { name: "Admissions Process", href: "/admisionprocess" },
        { name: "Fee Structure", href: "/fee-structure" }
      ] 
    },
    { name: "Facilities", href: "/facilities",
      subItems: [
        { name: "Library", href: "/library" },
        { name: "Class Room", href: "/classroom" },
        { name: "Games & Sports", href: "/gamespots" },
      ] 
    },
    { name: "Staff", href: "/staff",
      subItems: [
        { name: "Teaching Staff", href: "/cellassociation" },
        { name: "Non-Teaching Staff", href: "/cellassociation" }
      ] 
    },
    { name: "News", href: "/news" },
    { name: "Downloads", href: "/downloads",
      subItems: [
        { name: "Prospectus", href: "/prospectus" },
        { name: "Student Form", href: "/registration" },
        { name: "Resource Centre", href: "/downloads#resource" }
      ] 
    },
    { name: "Gallery", href: "/gallery" },
    { name: "Contact", href: "/contact" }
  ];

  const handleDropdownToggle = (name, e) => {
    e.stopPropagation();
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const handleMobileDropdownToggle = (name) => {
    setMobileDropdown(mobileDropdown === name ? null : name);
  };

  const handleNavigation = (href) => {
    setOpenDropdown(null);
    setIsOpen(false);
    setMobileDropdown(null);
    navigate(href);
  };

  return (
    <div className="relative">
      {/* Top Bar - Hidden on mobile, visible on tablet/desktop */}
      <div className="hidden sm:block bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white py-1 px-4 border-b border-yellow-500/30">
        <div className="container mx-auto flex justify-between items-center text-xs">
          <div className="flex flex-wrap gap-4">
            <span className="hover:text-yellow-300 transition-colors duration-300 cursor-pointer">
              {contactInfo.phone}
            </span>
            <span className="hover:text-yellow-300 transition-colors duration-300 cursor-pointer truncate max-w-[200px]">
              {contactInfo.email}
            </span>
            <span className="bg-gradient-to-r from-yellow-600 to-yellow-500 px-2 py-0.5 rounded-full shadow-lg font-semibold text-blue-900">
              College Code: {contactInfo.collegeCode}
            </span>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => navigate('/registration')}
              className="px-3 py-0.5 rounded-full bg-yellow-500 text-blue-900 font-semibold hover:bg-yellow-400 transition-all duration-300 text-xs"
            >
              Registration
            </button>
             <button 
              onClick={() => navigate('/commitee')}
              className="px-3 py-0.5 rounded-full bg-yellow-500 text-blue-900 font-semibold hover:bg-yellow-400 transition-all duration-300 text-xs"
            >
              UGC Committee
            </button>
            <button 
              onClick={() => navigate('/enquiryform')}
              className="px-3 py-0.5 rounded-full border-2 border-yellow-500 text-yellow-300 hover:bg-yellow-500 hover:text-blue-900 transition-all duration-300 text-xs"
            >
              Enquiry
            </button>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className={`bg-white transition-all duration-300 sticky top-0 z-50 ${isScrolled ? 'shadow-2xl shadow-blue-900/10' : 'shadow-lg'}`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group cursor-pointer">
              <img 
                src="/logo.png" 
                alt="BNS Mahila Maha Vidyalaya Logo" 
                className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                onError={(e) => e.target.src = 'https://via.placeholder.com/40?text=Logo'}
              />
            </Link>

            {/* Desktop Menu - Hidden on mobile/tablet */}
            <div className="hidden lg:flex items-center space-x-1" ref={dropdownRef}>
              {navItems.map((item) => (
                <div key={item.name} className="relative">
                  {item.subItems ? (
                    <>
                      <button
                        onClick={(e) => handleDropdownToggle(item.name, e)}
                        className={`px-3 py-1.5 text-gray-700 hover:text-blue-600 font-medium rounded-lg transition-all duration-300 text-sm ${
                          openDropdown === item.name ? 'bg-blue-50 text-blue-600' : 'hover:bg-blue-50'
                        }`}
                      >
                        {item.name}
                        <ChevronDown size={12} className={`ml-1 inline-block transition-all duration-300 ${openDropdown === item.name ? 'rotate-180 text-blue-600' : ''}`} />
                      </button>
                      
                      {/* Dropdown */}
                      {openDropdown === item.name && (
                        <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-2xl border border-gray-100 py-2 z-50">
                          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-full"></div>
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              onClick={() => setOpenDropdown(null)}
                              className="block px-4 py-2 text-xs text-gray-600 hover:bg-gradient-to-r hover:from-yellow-50 hover:to-transparent hover:text-yellow-600 transition-all duration-300"
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className="px-3 py-1.5 text-gray-700 hover:text-blue-600 font-medium rounded-lg transition-all duration-300 hover:bg-blue-50 text-sm"
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-blue-50 focus:outline-none transition-all duration-300"
            >
              {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isOpen && (
            <div 
              ref={mobileMenuRef}
              className="lg:hidden py-4 border-t border-gray-100 max-h-[80vh] overflow-y-auto"
            >
              {navItems.map((item) => (
                <div key={item.name}>
                  {item.subItems ? (
                    <>
                      <button
                        onClick={() => handleMobileDropdownToggle(item.name)}
                        className="flex items-center justify-between w-full px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent rounded-lg transition-all duration-300"
                      >
                        <span className="font-medium">{item.name}</span>
                        <ChevronDown size={16} className={`transition-all duration-300 ${mobileDropdown === item.name ? 'rotate-180 text-blue-600' : ''}`} />
                      </button>
                      {mobileDropdown === item.name && (
                        <div className="ml-6 pl-4 border-l-2 border-blue-200 mb-2 space-y-1">
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              onClick={() => handleNavigation(subItem.href)}
                              className="block px-4 py-2 text-sm text-gray-500 hover:text-blue-600 transition-all duration-300"
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => handleNavigation(item.href)}
                      className="block px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent rounded-lg transition-all duration-300"
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Mobile Action Buttons */}
              <div className="mt-6 pt-4 border-t border-gray-100 px-4">
                <div className="flex flex-col gap-3">
                  <button 
                    onClick={() => handleNavigation('/registration')}
                    className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-blue-900 px-4 py-2.5 rounded-lg font-semibold text-sm hover:shadow-md transition-all duration-300"
                  >
                    Registration
                  </button>
                  <button 
                    onClick={() => handleNavigation('/enquiry')}
                    className="border-2 border-blue-600 text-blue-600 px-4 py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-600 hover:text-white transition-all duration-300"
                  >
                    Enquiry
                  </button>
                </div>
              </div>

              {/* Mobile Contact Info */}
              <div className="mt-4 pt-4 border-t border-gray-100 px-4 space-y-2">
                <p className="text-gray-500 text-xs">{contactInfo.phone}</p>
                <p className="text-gray-500 text-xs break-all">{contactInfo.email}</p>
                <p className="text-blue-600 text-xs font-semibold">College Code: {contactInfo.collegeCode}</p>
              </div>
            </div>
          )}
        </div>
      </nav>
    </div>
  );
};

// Simple ChevronDown component
const ChevronDown = ({ size, className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

export default Navbar;